import { CylinderGeometry, Mesh, Vector3 } from 'three';
import { gsap } from 'gsap';
import CoinTextureManager from './CoinTextureManager.js';

// Constants
const COIN_RADIUS = 0.38;
const COIN_HEIGHT = 0.1;
const INITIAL_SCALE = 0.0001;
const BASE_SCALE = 1;
const MIN_COIN_SCALE = 0.5; // Minimum scale value (for 20 coins)
const MAX_COIN_SCALE = 1; // Maximum scale value (for 1 coin)
const Z_MIN = -1;
const Z_MAX = 1;
const PARABOLA_DURATION = 5.5; // seconds
const END_SIDE_FLIP_CHANCE = 0.2; // Chance to end on opposite side from start
const END_RANGE_MULTIPLIER = 0.6; // End position range as fraction of viewport width
const END_RANGE_MULTIPLIER_PORTRAIT = 0.9; // End position range for portrait orientation
const MIN_PEAK_Y_MULTIPLIER = 0.6; // Minimum peak Y position multiplier
const MAX_PEAK_Y_MULTIPLIER = 1.4; // Maximum peak Y position multiplier
const MIN_PEAK_Y_MULTIPLIER_PORTRAIT = 0.5; // Minimum peak Y position multiplier for portrait
const MAX_PEAK_Y_MULTIPLIER_PORTRAIT = 1.6; // Maximum peak Y position multiplier for portrait
const PEAK_X_MIN_OFFSET = 0.3; // Minimum peak X offset factor
const PEAK_X_MAX_OFFSET = 0.4; // Maximum peak X offset factor
const PARABOLA_PROGRESS_MULTIPLIER = 2; // Multiplier to extend parabola off screen
const ROTATION_Z_MULTIPLIER = 2.5; // Rotation Z multiplier
const ROTATION_BASE_ANGLE = 0.5; // Base rotation angle in PI units
const ROTATION_RANDOM_RANGE = 0.5; // Random rotation range in PI units
const START_Z = 0; // Starting Z position

class Coin {
  geometry = null;
  materials = null;
  mesh = null;
  startPosition = null;
  animationTimeline = null;
  isAnimating = false;
  coinScale = BASE_SCALE;
  endRangeMultiplier = END_RANGE_MULTIPLIER;
  minPeakYMultiplier = MIN_PEAK_Y_MULTIPLIER;
  maxPeakYMultiplier = MAX_PEAK_Y_MULTIPLIER;

  constructor({ scene, position, viewportInfo, textureManager, coinScale = BASE_SCALE, endRangeMultiplier = END_RANGE_MULTIPLIER, minPeakYMultiplier = MIN_PEAK_Y_MULTIPLIER, maxPeakYMultiplier = MAX_PEAK_Y_MULTIPLIER }) {
    this.geometry = new CylinderGeometry(COIN_RADIUS, COIN_RADIUS, COIN_HEIGHT);
    this.materials = textureManager.getCoinMaterials();
    this.mesh = new Mesh(this.geometry, this.materials);

    // Store start position
    this.startPosition = position.clone();

    // Position the coin
    this.mesh.position.copy(position);

    // Start at tiny scale to hide (will be set to 1 when animateAppearance is called)
    this.mesh.scale.set(INITIAL_SCALE, INITIAL_SCALE, INITIAL_SCALE);

    // Ensure coin is visible (not culled)
    this.mesh.frustumCulled = false;

    // Store viewport info for parabola calculation
    this.viewportInfo = viewportInfo;

    // Store coin scale
    this.coinScale = coinScale;

    // Store end range multiplier
    this.endRangeMultiplier = endRangeMultiplier;

    // Store peak Y multipliers
    this.minPeakYMultiplier = minPeakYMultiplier;
    this.maxPeakYMultiplier = maxPeakYMultiplier;

    // Add to scene
    scene.add(this.mesh);
  }

  /**
   * Calculate parabola parameters
   * Parabola starts at coin's start position, peaks above viewport, and ends at bottom
   */
  calculateParabola() {
    const { visibleWidth, visibleHeight, bottomY, startY } = this.viewportInfo;

    // Use the coin's actual start X position
    const startX = this.startPosition.x;

    // Determine if coin is on left or right side based on start position
    const isLeft = startX < 0;

    // Chance to end on opposite side from start
    const endIsLeft = Math.random() < END_SIDE_FLIP_CHANCE ? !isLeft : isLeft;

    // Calculate endX on the determined side
    // End range is a fraction of viewport width, split evenly on each side
    const endRange = visibleWidth * this.endRangeMultiplier;
    const endXWorld = (Math.random() * (endRange / 2)) * (endIsLeft ? -1 : 1);

    // Set peak control point range
    // The actual curve maximum will be lower due to bezier behavior
    // Using multipliers > 1 ensures peaks are well above the middle line
    const topY = -bottomY;
    const minPeakY = topY * this.minPeakYMultiplier;
    const maxPeakY = topY * this.maxPeakYMultiplier;
    const peakY = minPeakY + Math.random() * (maxPeakY - minPeakY);

    const peakX = startX + (endXWorld - startX) * (PEAK_X_MIN_OFFSET + Math.random() * PEAK_X_MAX_OFFSET);
    const endZ = Z_MIN + Math.random() * (Z_MAX - Z_MIN);

    return {
      startX: startX,
      startY: startY,
      startZ: START_Z,
      peakX: peakX,
      peakY: peakY,
      endX: endXWorld,
      endY: bottomY,
      endZ: endZ,
    };
  }

  /**
   * Start the appearance and parabola animation
   * @param {number} delay - Delay before starting (for staggering)
   */
  animateAppearance(delay = 0) {
    if (this.isAnimating) return;
    this.isAnimating = true;

    this.mesh.scale.set(INITIAL_SCALE, INITIAL_SCALE, INITIAL_SCALE);


    const parabola = this.calculateParabola();

    // Face coin toward the peak position (including Z)
    const endPosition = new Vector3(parabola.endX, parabola.endY, parabola.endZ);
    const startPosition = new Vector3(parabola.startX, parabola.startY, parabola.startZ);
    const direction = new Vector3().subVectors(endPosition, startPosition).normalize();

    // Use lookAt to face the coin toward the peak
    const targetPosition = new Vector3().addVectors(startPosition, direction);
    this.mesh.lookAt(targetPosition);

    // Rotate to align cylinder's Y axis with the direction
    this.mesh.rotateY(Math.PI / 2);

    // Additional rotation so the "face" (top of cylinder) points toward peak
    this.mesh.rotateZ(Math.PI * -ROTATION_BASE_ANGLE);

    // Create timeline with delay for the parabola animation
    this.animationTimeline = gsap.timeline({ delay, onStart: () => {
        // Scale up to coin size instantly (no animation, no delay)
      // This happens immediately when called, not in the timeline
      this.mesh.scale.set(this.coinScale, this.coinScale, this.coinScale);
    } });

    // Animate along parabola using quadratic bezier
    // Use a dummy object to track progress, then update mesh position manually
    const progressObj = { progress: 0 };

    this.animationTimeline.to(
      progressObj,
      {
        progress: 1,
        duration: PARABOLA_DURATION,
        ease: 'power3.out', // Slight ease for natural motion
        onUpdate: () => {
          const t = progressObj.progress * PARABOLA_PROGRESS_MULTIPLIER;

          // Quadratic bezier for smooth parabolic motion
          // P(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
          // Where P0 = start, P1 = peak, P2 = end
          const x =
            Math.pow(1 - t, 2) * parabola.startX +
            2 * (1 - t) * t * parabola.peakX +
            Math.pow(t, 2) * parabola.endX;
          const y =
            Math.pow(1 - t, 2) * parabola.startY +
            2 * (1 - t) * t * parabola.peakY +
            Math.pow(t, 2) * parabola.endY;
          const z = parabola.startZ + (parabola.endZ - parabola.startZ) * t; // Linear interpolation for Z

          this.mesh.position.x = x;
          this.mesh.position.y = y;
          this.mesh.position.z = z;
        },
      }
    );
    const z = this.mesh.position.z + Math.PI * ROTATION_Z_MULTIPLIER;
    const x = this.mesh.rotation.x + Math.PI * ROTATION_BASE_ANGLE - Math.random() * Math.PI * ROTATION_RANDOM_RANGE;
    const y = this.mesh.rotation.y + Math.PI * ROTATION_BASE_ANGLE - Math.random() * Math.PI * ROTATION_RANDOM_RANGE;

    this.animationTimeline.to(this.mesh.rotation, {
      z: z,
      x: x,
      y: y,
      duration: PARABOLA_DURATION,
      ease: 'power3.out',
    }, '<');
  }

  update() {


  }

  dispose() {
    if (this.animationTimeline) {
      this.animationTimeline.kill();
      this.animationTimeline = null;
    }

    if (this.geometry) {
      this.geometry.dispose();
    }
    if (this.material) {
      this.material.dispose();
    }
  }
}

export default Coin;
export { MIN_COIN_SCALE, MAX_COIN_SCALE, END_RANGE_MULTIPLIER, END_RANGE_MULTIPLIER_PORTRAIT, MIN_PEAK_Y_MULTIPLIER, MAX_PEAK_Y_MULTIPLIER, MIN_PEAK_Y_MULTIPLIER_PORTRAIT, MAX_PEAK_Y_MULTIPLIER_PORTRAIT };

